import { useEffect, useState } from "react";
import Link from "next/link";

import getRecommendedStrains from "api/requests/consumerApi/getRecommendedStrains";
import { Strain } from "custom-types/Strain";
import { useDispatch, useSelector } from "redux/hooks";
import useUserFavorites from "redux/hooks/useUserFavorites";
import useUserStrainPreferences from "redux/hooks/useUserStrainPreferences";
import { REQUEST_LOCATION } from "redux/reducers/location";
import { getCoordinates } from "redux/selectors/location";
import {
  getUserFavoriteStrains,
  getUserId,
  getUserStrainPreferences,
} from "redux/selectors/user";
import { createGetServerSideProps } from "utils/createGetServerSideProps";

import SectionHeader from "components/botanic/SectionHeader";
import StrainCard, {
  StrainCardAccessoryTray,
} from "components/botanic/StrainCard";
import ProfileStrainQuizPreferences from "components/StrainQuiz/ProfileStrainQuizPreferences";
import StrainQuizContainer from "components/StrainQuiz/StrainQuizContainer";
import StrainQuizResultsLoggedOutCTA from "components/StrainQuiz/StrainQuizResultsLoggedOutCTA";
import UserProfileLayout from "components/UserProfile/UserProfileLayout";

const RecommendedStrainsPage: React.FC = () => {
  const coordinates = useSelector(getCoordinates);
  const favoritedStrains = useSelector(getUserFavoriteStrains);
  const strainPreferences = useSelector(getUserStrainPreferences);
  const userId = useSelector(getUserId);
  const dispatch = useDispatch();
  const [strains, setStrains] = useState<Strain[]>([]);
  const [pageDataLoaded, setPageDataLoaded] = useState<boolean>(false);

  const { retrieveFavorites } = useUserFavorites();
  useUserStrainPreferences();

  useEffect(() => {
    // ensure that favorite strains are in redux for rendering our strain cards:
    if (!favoritedStrains) {
      retrieveFavorites();
    }
  }, []);

  useEffect(() => {
    if (strainPreferences?.preferences?.length) {
      getRecommendedStrains({
        data: { userPrefs: strainPreferences.preferences },
        lat: coordinates.lat,
        lon: coordinates.lon,
        productCategory: [],
        version: "OriginalStrainQuiz",
      })
        .then((res) => {
          setStrains(res.strains);
        })
        .catch(() => {})
        .finally(() => {
          setPageDataLoaded(true);
        });
    } else if (
      !strainPreferences?.loading &&
      strainPreferences?.preferences?.length === 0
    ) {
      // prefs are loaded, but they are empty
      setPageDataLoaded(true);
    }
  }, [strainPreferences]);

  if (!userId) {
    /*
    the user profile pages are visible to logged-out users, if they can find them.
    prompt user to login to use strain reco.
    */
    return (
      <UserProfileLayout currentPage="Recommended strains">
        <div className="text-center">
          <StrainQuizResultsLoggedOutCTA isGated={true} />
        </div>
      </UserProfileLayout>
    );
  } else {
    return (
      <UserProfileLayout currentPage="Recommended strains">
        {pageDataLoaded ? (
          <>
            {strainPreferences?.preferences?.length ? (
              <ProfileStrainQuizPreferences />
            ) : (
              <StrainQuizContainer showResults={false}></StrainQuizContainer>
            )}
            {strains?.length ? (
              <>
                <SectionHeader className="mb-lg">
                  Recommended for you
                </SectionHeader>
                <div className="row">
                  {strains?.map((strain) => (
                    <div
                      className="col-6 md:col-1/2 lg:col-1/3 mb-md"
                      key={strain.slug}
                    >
                      <StrainCard
                        orientation={{
                          desktop: "vertical",
                          mobile: "horizontal",
                        }}
                        preserveHeight={{ desktop: true, mobile: false }}
                        href={{
                          pathname: "/shop",
                          query: {
                            "filter[strain_name]": strain.name,
                            fulfillmentPreference: "pickup",
                          },
                        }}
                        strain={strain}
                        showMatchScore={true}
                        trackingLabel="recommended strains page"
                        trackingCategory="strain_card"
                        accessoryTray={
                          <StrainCardAccessoryTray
                            strain={strain}
                            hasMenuItems={true}
                            trackingCategory="strain_card"
                            trackingAction="click"
                            trackingLabel="recommended strains page - shop menus"
                          />
                        }
                      />
                    </div>
                  ))}
                </div>
              </>
            ) : strainPreferences?.preferences?.length ? ( // have prefs, but no strain results.
              <>
                <div className="text-center">
                  We couldn't find any weed near you. Bummer!
                  <div className="mt-6 m-auto grid grid-cols-2 mb-10">
                    <button
                      className="button button--text block text-sm"
                      onClick={() => {
                        dispatch({ type: REQUEST_LOCATION });
                      }}
                    >
                      Change location
                    </button>
                    <Link
                      href="/strains"
                      className="button button--text block text-sm"
                    >
                      Browse all strains
                    </Link>
                  </div>
                </div>
              </>
            ) : (
              // there weren't any prefs so don't show reco section:
              <></>
            )}
          </>
        ) : (
          // page data not loaded yet:
          <></>
        )}
      </UserProfileLayout>
    );
  }
};

export const getServerSideProps = createGetServerSideProps();

export default RecommendedStrainsPage;
